/**
 * Principal Dashboard - Sanjaya Brand Kit
 * Navy (#254B70), Orange (#F0A820), Teal (#258010)
 * 
 * UX Principles Applied:
 * 1. Progressive Disclosure - Complex features hidden until needed
 * 2. Visual Hierarchy - Size and color show importance
 * 3. Cognitive Load - Minimal mental effort, clear actions
 * 4. Whitespace - Generous spacing for breathing room
 * 5. Primary vs Secondary Actions - Clear distinction
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Bell, FileText, Eye, ChevronRight, ChevronDown,
  Home, ClipboardList, UserCheck, Phone, BarChart3,
  Shield, Brain, AlertTriangle, Sparkles, Calendar, BookOpen
} from 'lucide-react';
import ResponsiveLayout from '../components/ResponsiveLayout';
import CallHistory from '../components/CallHistory';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Primary Action Card - Brand colors
 */
const PrimaryActionCard = ({ title, count, description, icon: Icon, onClick, urgent = false }) => (
  <button
    onClick={onClick}
    className={`
      w-full p-6 sm:p-8 rounded-2xl text-left
      transition-all duration-300 hover:scale-[1.01] active:scale-[0.99]
      focus:outline-none focus:ring-4 focus:ring-offset-2
      ${urgent 
        ? 'bg-gradient-to-br from-red-500 to-red-600 focus:ring-red-300 shadow-lg' 
        : 'bg-sanjaya-navy focus:ring-sanjaya-navy/30 shadow-lg'
      }
    `}
    data-testid={`primary-action-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-4">
      <span className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/15">
        <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
      </span>
      {count > 0 && (
        <span className="bg-sanjaya-orange text-sanjaya-navy text-lg sm:text-xl font-black px-4 py-2 rounded-full shadow-md">
          {count}
        </span>
      )}
    </div>
    
    <h2 className="text-2xl sm:text-3xl font-bold font-heading mb-2 text-white">{title}</h2>
    <p className="text-base sm:text-lg text-white/90 mb-4">{description}</p>
    
    <div className="flex items-center text-sanjaya-orange text-base sm:text-lg font-semibold">
      <span>Review Now</span>
      <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 ml-2" />
    </div>
  </button>
);

/**
 * Quick Action Icon Button - Compact style
 */
const QuickActionIcon = ({ label, icon: Icon, onClick, badge }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center justify-center p-3 rounded-xl bg-white border border-slate-200 hover:border-sanjaya-navy/30 hover:shadow-md transition-all duration-200 active:scale-95 focus:outline-none focus:ring-2 focus:ring-sanjaya-navy/20 relative"
    data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {badge > 0 && (
      <span className="absolute -top-1 -right-1 w-5 h-5 bg-sanjaya-orange text-white text-xs font-bold rounded-full flex items-center justify-center">
        {badge > 99 ? '99+' : badge}
      </span>
    )}
    <span className="w-10 h-10 rounded-lg bg-sanjaya-navy/10 flex items-center justify-center mb-1">
      <Icon className="w-5 h-5 text-sanjaya-navy" />
    </span>
    <span className="text-xs font-medium text-slate-600 text-center leading-tight">{label}</span>
  </button>
);

/**
 * Upcoming Item Card - For meetings and sessions
 */
const UpcomingItemCard = ({ title, time, subtitle, icon: Icon, onClick }) => (
  <button
    onClick={onClick}
    className="w-full p-4 rounded-xl text-left bg-white border border-slate-200 hover:border-sanjaya-orange/50 hover:shadow-sm transition-all"
  >
    <div className="flex items-center gap-3">
      <span className="w-10 h-10 rounded-lg bg-sanjaya-orange/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-sanjaya-orange" />
      </span>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-slate-800 truncate">{title}</h4>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </div>
      <span className="text-xs font-medium text-sanjaya-navy bg-sanjaya-navy/10 px-2 py-1 rounded">
        {time}
      </span>
    </div>
  </button>
);

/**
 * Stats Summary - Progressive Disclosure
 */
const StatsSummary = ({ earnings, students, observers, sessions, onNavigate }) => {
  const [expanded, setExpanded] = useState(false);
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden shadow-sm">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full p-5 sm:p-6 text-left hover:bg-slate-50 transition-colors focus:outline-none"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm sm:text-base text-slate-500 font-medium mb-1">Total Earnings</p>
            <p className="text-2xl sm:text-3xl font-bold text-sanjaya-navy font-heading">{formatCurrency(earnings)}</p>
          </div>
          <div className={`p-2 rounded-full bg-sanjaya-navy/10 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>
            <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6 text-sanjaya-navy" />
          </div>
        </div>
      </button>
      
      {expanded && (
        <div className="border-t-2 border-slate-100 p-5 sm:p-6 bg-slate-50 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <button
              onClick={() => onNavigate('/principal/session-history')}
              className="text-center p-3 rounded-xl bg-white hover:bg-sanjaya-navy/5 transition-colors border border-slate-200"
            >
              <p className="text-2xl sm:text-3xl font-bold text-sanjaya-navy font-heading">{students}</p>
              <p className="text-xs sm:text-sm text-slate-600 font-medium">Students</p>
            </button>
            <button 
              onClick={() => onNavigate('/principal/observer-performance')}
              className="text-center p-3 rounded-xl bg-white hover:bg-sanjaya-navy/5 transition-colors border border-slate-200"
            >
              <p className="text-2xl sm:text-3xl font-bold text-sanjaya-teal font-heading">{observers}</p>
              <p className="text-xs sm:text-sm text-slate-600 font-medium">Observers</p>
            </button>
            <button 
              onClick={() => onNavigate('/principal/session-history')}
              className="text-center p-3 rounded-xl bg-white hover:bg-sanjaya-navy/5 transition-colors border border-slate-200"
            >
              <p className="text-2xl sm:text-3xl font-bold text-sanjaya-orange font-heading">{sessions}</p>
              <p className="text-xs sm:text-sm text-slate-600 font-medium">Sessions</p>
            </button>
          </div>
          <button 
            onClick={() => onNavigate('/principal/business')}
            className="w-full py-3 text-center text-sanjaya-navy font-semibold hover:bg-sanjaya-navy/5 rounded-xl transition-colors"
          >
            View Full Business Report →
          </button>
        </div>
      )}
    </div>
  );
};

const PrincipalDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [pendingCounts, setPendingCounts] = useState({
    reviewCenter: 0,
    flags: 0
  });
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [earnings, setEarnings] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadDashboardData();
  }, [navigate]);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('principal_token');
      
      const [dashboardRes, reviewCenterRes, earningsRes, meetingsRes, sessionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/principal/dashboard?token=${token}`),
        axios.get(`${BACKEND_URL}/api/principal/review-center/sessions?token=${token}&limit=1`).catch(() => ({ data: { total: 0 } })),
        axios.get(`${BACKEND_URL}/api/principal/earnings-summary?token=${token}&period=all`).catch(() => ({ data: { earnings: {} } })),
        axios.get(`${BACKEND_URL}/api/principal/meetings?token=${token}&status=upcoming&limit=3`).catch(() => ({ data: { meetings: [] } })),
        axios.get(`${BACKEND_URL}/api/principal/upcoming-sessions?token=${token}&limit=3`).catch(() => ({ data: { sessions: [] } })),
      ]);
      
      setData(dashboardRes.data);

      setPendingCounts({
        reviewCenter: reviewCenterRes.data.total || 0,
        flags: 0,
      });
      
      // Set upcoming meetings
      setUpcomingMeetings(meetingsRes.data.meetings || []);
      
      // Set upcoming sessions
      setUpcomingSessions(sessionsRes.data.sessions || []);
      
      const e = earningsRes.data.earnings || {};
      setEarnings({
        total: e.grand_total || e.total_earnings || 0
      });
      
    } catch (error) {
      console.error('Error loading dashboard:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('principal_token');
        navigate('/principal/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const handleLogout = () => {
    localStorage.removeItem('principal_token');
    navigate('/principal/login');
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-sanjaya-navy/20 border-t-sanjaya-navy rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-xl text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { principal, statistics } = data;
  const hasUrgentTasks = pendingCounts.flags > 0 || pendingCounts.reviewCenter > 0;

  return (
    <ResponsiveLayout
      navItems={principalNavItems}
      userRole="principal"
      userName={principal?.name || 'Principal'}
      onLogout={handleLogout}
    >
      <div className="max-w-3xl mx-auto" data-testid="principal-dashboard">
        
        {/* Greeting */}
        <header className="mb-8 sm:mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 font-heading mb-2">
            {getGreeting()},
          </h1>
          <h2 className="text-3xl sm:text-4xl font-bold text-sanjaya-navy font-heading">
            {principal?.name?.split(' ')[0]}
          </h2>
          <p className="text-base sm:text-lg text-slate-500 mt-3">
            {new Date().toLocaleDateString('en-IN', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long'
            })}
          </p>
        </header>

        {/* PRIMARY ACTIONS - Review Center */}
        {hasUrgentTasks && (
          <section className="mb-8 sm:mb-10">
            <h3 className="text-lg sm:text-xl font-bold text-slate-700 font-heading mb-4 flex items-center gap-2">
              <span className="w-2 h-2 bg-sanjaya-orange rounded-full animate-pulse"></span>
              Needs Your Attention
            </h3>
            
            <div className="space-y-4">
              {pendingCounts.reviewCenter > 0 && (
                <PrimaryActionCard
                  title="Pending Approvals"
                  count={pendingCounts.reviewCenter}
                  description="Sessions waiting for your review in Review Center"
                  icon={Sparkles}
                  onClick={() => navigate('/principal/review-center')}
                />
              )}
            </div>
          </section>
        )}

        {/* ALL CAUGHT UP */}
        {!hasUrgentTasks && (
          <section className="mb-8 sm:mb-10">
            <div className="bg-sanjaya-teal/10 border-2 border-sanjaya-teal/30 rounded-2xl p-8 sm:p-10 text-center">
              <div className="w-20 h-20 bg-sanjaya-teal/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-sanjaya-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-sanjaya-teal-dark font-heading mb-2">All Caught Up!</h3>
              <p className="text-base sm:text-lg text-sanjaya-teal">No pending reviews. Great job!</p>
            </div>
          </section>
        )}

        {/* UPCOMING MEETINGS */}
        {upcomingMeetings.length > 0 && (
          <section className="mb-8 sm:mb-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-slate-700 font-heading flex items-center gap-2">
                <Calendar className="w-5 h-5 text-sanjaya-orange" />
                Upcoming Meetings
              </h3>
              <button 
                onClick={() => navigate('/principal/meetings')}
                className="text-sm text-sanjaya-navy font-medium hover:underline"
              >
                View All
              </button>
            </div>
            <div className="space-y-2">
              {upcomingMeetings.map((meeting, idx) => (
                <UpcomingItemCard
                  key={meeting._id || idx}
                  title={meeting.title || meeting.subject || 'Meeting'}
                  subtitle={meeting.with_name || meeting.attendee || 'Team Meeting'}
                  time={formatDate(meeting.scheduled_at || meeting.date)}
                  icon={Calendar}
                  onClick={() => navigate('/principal/meetings')}
                />
              ))}
            </div>
          </section>
        )}

        {/* UPCOMING SESSIONS */}
        {upcomingSessions.length > 0 && (
          <section className="mb-8 sm:mb-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-slate-700 font-heading flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-sanjaya-teal" />
                Upcoming Sessions
              </h3>
              <button 
                onClick={() => navigate('/principal/session-history')}
                className="text-sm text-sanjaya-navy font-medium hover:underline"
              >
                View All
              </button>
            </div>
            <div className="space-y-2">
              {upcomingSessions.map((session, idx) => (
                <UpcomingItemCard
                  key={session._id || idx}
                  title={session.child_name || 'Session'}
                  subtitle={`Observer: ${session.observer_name || 'TBD'}`}
                  time={formatDate(session.scheduled_at || session.session_date)}
                  icon={ClipboardList}
                  onClick={() => navigate('/principal/session-history')}
                />
              ))}
            </div>
          </section>
        )}

        {/* OVERVIEW */}
        <section className="mb-8 sm:mb-10">
          <h3 className="text-lg sm:text-xl font-bold text-slate-700 font-heading mb-4">
            Overview
          </h3>
          <StatsSummary
            earnings={earnings.total}
            students={statistics?.total_students || 0}
            observers={statistics?.total_observers || 0}
            sessions={statistics?.appointments_this_month || 0}
            onNavigate={navigate}
          />
        </section>

        {/* QUICK ACTIONS - Compact Icons */}
        <section className="mb-8">
          <h3 className="text-lg sm:text-xl font-bold text-slate-700 font-heading mb-4">
            Quick Actions
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            <QuickActionIcon
              label="Review"
              icon={Sparkles}
              onClick={() => navigate('/principal/review-center')}
            />
            <QuickActionIcon
              label="Sessions"
              icon={ClipboardList}
              onClick={() => navigate('/principal/session-history')}
            />
            <QuickActionIcon
              label="Meetings"
              icon={Calendar}
              onClick={() => navigate('/principal/meetings')}
            />
            <QuickActionIcon
              label="Observers"
              icon={UserCheck}
              onClick={() => navigate('/principal/observer-performance')}
            />
            <QuickActionIcon
              label="Policy"
              icon={BookOpen}
              onClick={() => navigate('/principal/policy-book')}
            />
          </div>
        </section>

        {/* CALL HISTORY with Recordings */}
        <section className="mb-8 sm:mb-10">
          <CallHistory userType="principal" token={localStorage.getItem('principal_token')} />
        </section>

        <div className="h-8 sm:h-12"></div>
      </div>
    </ResponsiveLayout>
  );
};

export default PrincipalDashboard;
